/**
 * Schema.org JSON-LD builders. All builders return plain JSON-serializable
 * objects (or `null` when there is not enough data). They never throw —
 * if a critical field is missing they return `null` so callers can
 * conditionally render via Page's `structuredData` prop.
 *
 * Inputs are intentionally loose (`any`/`Record`) because Tina's
 * generated types drift as the schema evolves; each builder defensively
 * extracts only the fields it needs.
 */

import { extractTextFromTiptap } from "@/utils";
import { RESOURCE_TYPES } from "@/lib/resource-types";
import type { BreadcrumbItem } from "./breadcrumbs";
import {
  absoluteShareImageUrl,
  type GlobalSeoInput,
  type SeoInput,
} from "./metadata";
import { SITE_NAME, absoluteUrl, getSiteUrl } from "./site";

export interface ShareImageContext {
  parentSeo?: SeoInput | null;
  globalSeo?: GlobalSeoInput | null;
}

function schemaShareImage(
  seo: SeoInput | null | undefined,
  contentImage: string | null | undefined,
  ctx?: ShareImageContext,
): string | undefined {
  const url = absoluteShareImageUrl({
    seo,
    contentImage,
    parentSeo: ctx?.parentSeo,
    globalSeo: ctx?.globalSeo,
  });
  return url || undefined;
}

const SCHEMA_CONTEXT = "https://schema.org";

const ORG_ID = "#organization";
const WEBSITE_ID = "#website";

function pick<T>(...values: Array<T | null | undefined>): T | undefined {
  for (const v of values) {
    if (v !== null && v !== undefined && v !== "") return v;
  }
  return undefined;
}

/** Aligns with `resolveDescription` in metadata: SEO override, then body fields. */
function editorialDescriptionFromResource(
  resource: {
    description?: string | null;
    summary?: string | null;
    resourceSeo?: { seoDescription?: string | null } | null;
  },
  ...extraFallbacks: Array<string | null | undefined>
): string | undefined {
  return pick(
    resource.resourceSeo?.seoDescription?.trim(),
    resource.description,
    resource.summary,
    ...extraFallbacks
  );
}

function toAbsolute(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  return absoluteUrl(value);
}

interface GlobalOrganizationInput {
  legalName?: string | null;
  description?: string | null;
  logo?: string | null;
  foundingDate?: string | null;
  founders?: Array<{ name?: string | null; url?: string | null }> | null;
  address?: {
    streetAddress?: string | null;
    addressLocality?: string | null;
    addressRegion?: string | null;
    postalCode?: string | null;
    addressCountry?: string | null;
  } | null;
  contact?: {
    email?: string | null;
    telephone?: string | null;
    contactType?: string | null;
  } | null;
  sameAs?: Array<{ url?: string | null }> | null;
  twitterSite?: string | null;
  twitterCreatorDefault?: string | null;
}

interface GlobalInput {
  organization?: GlobalOrganizationInput | null;
  seo?: {
    title?: string | null;
    description?: string | null;
    image?: string | null;
  } | null;
  footer?: {
    socials?: Array<{
      platform?: string | null;
      url?: string | null;
    }> | null;
  } | null;
}

/**
 * Collect a deduped `sameAs` list. Editorial `organization.sameAs`
 * entries take priority, then we merge in any absolute URLs from
 * `footer.socials` so the schema stays correct even when an editor
 * forgets to mirror a new social account.
 */
function collectSameAs(global: GlobalInput | null | undefined): string[] {
  const out = new Set<string>();
  const editorial = global?.organization?.sameAs || [];
  for (const item of editorial) {
    const url = item?.url?.trim();
    if (url && /^https?:\/\//i.test(url)) out.add(url);
  }
  const socials = global?.footer?.socials || [];
  for (const item of socials) {
    const url = item?.url?.trim();
    if (url && /^https?:\/\//i.test(url)) out.add(url);
  }
  return Array.from(out);
}

export function organizationSchema(
  global: GlobalInput | null | undefined
): Record<string, unknown> {
  const org = global?.organization || {};
  const siteUrl = getSiteUrl();
  const logo = toAbsolute(pick(org.logo, global?.seo?.image));
  const sameAs = collectSameAs(global);

  const address = org.address
    ? {
        "@type": "PostalAddress",
        streetAddress: org.address.streetAddress || undefined,
        addressLocality: org.address.addressLocality || undefined,
        addressRegion: org.address.addressRegion || undefined,
        postalCode: org.address.postalCode || undefined,
        addressCountry: org.address.addressCountry || undefined,
      }
    : undefined;

  const contact = org.contact && (org.contact.email || org.contact.telephone)
    ? {
        "@type": "ContactPoint",
        contactType: org.contact.contactType || "customer support",
        email: org.contact.email || undefined,
        telephone: org.contact.telephone || undefined,
      }
    : undefined;

  const founders = (org.founders || [])
    .map((founder) => {
      const name = founder?.name?.trim();
      if (!name) return null;
      const url = founder?.url?.trim();
      return {
        "@type": "Person",
        name,
        ...(url ? { url } : {}),
      };
    })
    .filter((f): f is { "@type": string; name: string; url?: string } => f !== null);

  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Organization",
    "@id": `${siteUrl}/${ORG_ID}`,
    name: org.legalName || SITE_NAME,
    url: siteUrl,
    ...(logo ? { logo } : {}),
    description:
      org.description || global?.seo?.description || undefined,
    ...(address ? { address } : {}),
    ...(contact ? { contactPoint: contact } : {}),
    ...(founders.length ? { founder: founders } : {}),
    ...(org.foundingDate ? { foundingDate: org.foundingDate } : {}),
    ...(sameAs.length ? { sameAs } : {}),
  };
}

export function webSiteSchema(
  global: GlobalInput | null | undefined
): Record<string, unknown> {
  const siteUrl = getSiteUrl();
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "WebSite",
    "@id": `${siteUrl}/${WEBSITE_ID}`,
    url: siteUrl,
    name: global?.organization?.legalName || SITE_NAME,
    publisher: { "@id": `${siteUrl}/${ORG_ID}` },
  };
}

export function breadcrumbSchema(
  trail: BreadcrumbItem[] | null | undefined
): Record<string, unknown> | null {
  if (!trail || trail.length === 0) return null;
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: trail.map((item, index) => {
      const entry: Record<string, unknown> = {
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
      };
      if (item.url) entry.item = item.url;
      return entry;
    }),
  };
}

export function webPageSchema({
  url,
  name,
  description,
  image,
}: {
  url: string;
  name: string;
  description?: string | null;
  image?: string | null;
}): Record<string, unknown> {
  const siteUrl = getSiteUrl();
  const absolute = absoluteUrl(url);
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "WebPage",
    "@id": absolute,
    url: absolute,
    name,
    description: description || undefined,
    image: image ? absoluteUrl(image) : undefined,
    isPartOf: { "@id": `${siteUrl}/${WEBSITE_ID}` },
    publisher: { "@id": `${siteUrl}/${ORG_ID}` },
  };
}

const ARTICLE_TYPE_BY_TEMPLATE: Record<string, string> = {
  article: "Article",
  blog: "BlogPosting",
  news: "NewsArticle",
  press: "NewsArticle",
  advisory: "TechArticle",
  patent: "Article",
  study: "Article",
  report: "Article",
};

interface ArticleResourceInput {
  templateType?: string | null;
  title?: string | null;
  description?: string | null;
  summary?: string | null;
  publishDate?: string | null;
  author?: string | null;
  resolvedAuthors?: Array<{
    type?: "teamMember" | "custom";
    name?: string;
    profileUrl?: string;
    isUnresolved?: boolean;
  }> | null;
  image?: string | null;
  resourceSeo?: { image?: string | null; seoDescription?: string | null } | null;
  categories?: Array<{
    category?: { title?: string | null } | null;
  }> | null;
  advisoryId?: string | null;
  cveId?: string | null;
  cvssScore?: number | null;
  affectedVendors?: Array<{ name?: string | null }> | null;
  affectedProducts?: Array<{ name?: string | null }> | null;
}

export function articleSchema({
  resource,
  pagePath,
  shareImageContext,
}: {
  resource: ArticleResourceInput | null | undefined;
  pagePath: string;
  shareImageContext?: ShareImageContext;
}): Record<string, unknown> | null {
  if (!resource?.title) return null;

  const siteUrl = getSiteUrl();
  const articleType =
    ARTICLE_TYPE_BY_TEMPLATE[resource.templateType || ""] || "Article";

  const description = editorialDescriptionFromResource(resource);

  const image = schemaShareImage(
    resource.resourceSeo,
    resource.image,
    shareImageContext,
  );

  const url = absoluteUrl(pagePath);
  const section = resource.categories?.[0]?.category?.title || undefined;

  const author = resource.resolvedAuthors?.length
    ? resource.resolvedAuthors.map((entry) => ({
        "@type": "Person" as const,
        name: entry.name || "",
        ...(entry.profileUrl && !entry.isUnresolved
          ? { url: entry.profileUrl.startsWith("http")
              ? entry.profileUrl
              : `${siteUrl}${entry.profileUrl}` }
          : {}),
      }))
    : resource.author?.trim()
      ? { "@type": "Person", name: resource.author.trim() }
      : { "@id": `${siteUrl}/${ORG_ID}` };

  const base: Record<string, unknown> = {
    "@context": SCHEMA_CONTEXT,
    "@type": articleType,
    headline: resource.title,
    description,
    image,
    url,
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
    datePublished: resource.publishDate || undefined,
    dateModified: resource.publishDate || undefined,
    author,
    publisher: { "@id": `${siteUrl}/${ORG_ID}` },
    inLanguage: "en",
    articleSection: section,
    isPartOf: { "@id": `${siteUrl}/${WEBSITE_ID}` },
  };

  if (resource.templateType === "advisory") {
    const about = [
      ...(resource.affectedVendors || []).map((v) =>
        v?.name ? { "@type": "Organization", name: v.name } : null
      ),
      ...(resource.affectedProducts || []).map((p) =>
        p?.name ? { "@type": "Product", name: p.name } : null
      ),
    ].filter(Boolean);

    const identifier =
      resource.cveId || resource.advisoryId
        ? {
            "@type": "PropertyValue",
            propertyID: resource.cveId ? "CVE" : "Advisory ID",
            value: resource.cveId || resource.advisoryId,
          }
        : undefined;

    return {
      ...base,
      keywords: [resource.advisoryId, resource.cveId]
        .filter(Boolean)
        .join(", ") || undefined,
      identifier,
      about: about.length ? about : undefined,
      proficiencyLevel: "Expert",
    };
  }

  if (resource.templateType === "study") {
    return {
      ...base,
      articleSection: "Case Study",
    };
  }

  return base;
}

interface ReportResourceInput {
  title?: string | null;
  description?: string | null;
  summary?: string | null;
  publishDate?: string | null;
  image?: string | null;
  externalUrl?: string | null;
  uploadReport?: string | null;
  resourceSeo?: {
    image?: string | null;
    seoDescription?: string | null;
  } | null;
  seo?: { description?: string | null; image?: string | null } | null;
}

export function reportSchema({
  resource,
  pagePath,
  shareImageContext,
}: {
  resource: ReportResourceInput | null | undefined;
  pagePath: string;
  shareImageContext?: ShareImageContext;
}): Record<string, unknown> | null {
  if (!resource?.title) return null;
  const siteUrl = getSiteUrl();
  const url = absoluteUrl(pagePath);
  const image = schemaShareImage(
    resource.resourceSeo || resource.seo,
    resource.image,
    shareImageContext,
  );
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "Report",
    headline: resource.title,
    name: resource.title,
    description: editorialDescriptionFromResource(
      resource,
      resource.seo?.description
    ),
    image,
    url,
    datePublished: resource.publishDate || undefined,
    publisher: { "@id": `${siteUrl}/${ORG_ID}` },
    isPartOf: { "@id": `${siteUrl}/${WEBSITE_ID}` },
    ...(resource.externalUrl ? { sameAs: resource.externalUrl } : {}),
    ...(resource.uploadReport
      ? { encoding: { "@type": "MediaObject", contentUrl: toAbsolute(resource.uploadReport) } }
      : {}),
  };
}

interface VideoResourceInput {
  title?: string | null;
  description?: string | null;
  publishDate?: string | null;
  url?: string | null;
  resourceSeo?: {
    image?: string | null;
    seoDescription?: string | null;
  } | null;
}

export function videoSchema({
  resource,
  pagePath,
  shareImageContext,
}: {
  resource: VideoResourceInput | null | undefined;
  pagePath: string;
  shareImageContext?: ShareImageContext;
}): Record<string, unknown> | null {
  if (!resource?.title) return null;
  const thumbnail = schemaShareImage(
    resource.resourceSeo,
    undefined,
    shareImageContext,
  );
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "VideoObject",
    name: resource.title,
    description: editorialDescriptionFromResource(resource),
    uploadDate: resource.publishDate || undefined,
    thumbnailUrl: thumbnail,
    contentUrl: resource.url || undefined,
    embedUrl: resource.url || undefined,
    url: absoluteUrl(pagePath),
  };
}

interface ToolResourceInput {
  title?: string | null;
  description?: string | null;
  logo?: string | null;
  externalUrl?: string | null;
  header?: { tagline?: string | null } | null;
  resourceSeo?: {
    image?: string | null;
    seoDescription?: string | null;
  } | null;
}

export function softwareApplicationSchema({
  resource,
  pagePath,
  shareImageContext,
}: {
  resource: ToolResourceInput | null | undefined;
  pagePath: string;
  shareImageContext?: ShareImageContext;
}): Record<string, unknown> | null {
  if (!resource?.title) return null;
  const siteUrl = getSiteUrl();
  const image = schemaShareImage(
    resource.resourceSeo,
    resource.logo,
    shareImageContext,
  );
  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "SoftwareApplication",
    name: resource.title,
    description: editorialDescriptionFromResource(
      resource,
      resource.header?.tagline
    ),
    image,
    applicationCategory: "SecurityApplication",
    operatingSystem: "Cross-platform",
    url: absoluteUrl(pagePath),
    ...(resource.externalUrl ? { downloadUrl: resource.externalUrl } : {}),
    publisher: { "@id": `${siteUrl}/${ORG_ID}` },
  };
}

interface FaqInput {
  showFaqs?: boolean | null;
  faqs?: Array<{
    question?: string | null;
    answer?: unknown;
  }> | null;
}

/**
 * Builds a FAQPage schema from the shared `faqs` block. Returns `null`
 * unless `showFaqs` is true AND at least one question/answer pair has
 * non-empty text on both sides.
 */
export function faqSchema(faq: FaqInput | null | undefined): Record<string, unknown> | null {
  if (!faq || !faq.showFaqs) return null;
  const items = (faq.faqs || [])
    .map((item) => {
      const question = item?.question?.trim();
      const answerText = extractTextFromTiptap(item?.answer).trim();
      if (!question || !answerText) return null;
      return {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answerText,
        },
      };
    })
    .filter((item): item is NonNullable<typeof item> => item !== null);

  if (items.length === 0) return null;

  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "FAQPage",
    mainEntity: items,
  };
}

interface CollectionItemInput {
  title?: string | null;
  templateType?: string | null;
  filename?: string | null;
}

/**
 * Build a CollectionPage with up to 20 hasPart entries. Used for
 * resource listing pages (`/blog`, `/advisories`, etc.) and category
 * pages.
 */
export function collectionPageSchema({
  url,
  name,
  description,
  items,
}: {
  url: string;
  name: string;
  description?: string | null;
  items?: CollectionItemInput[];
}): Record<string, unknown> {
  const absolute = absoluteUrl(url);
  const siteUrl = getSiteUrl();
  const trimmed = (items || []).slice(0, 20);

  const elements = trimmed
    .map((item) => {
      if (!item?.filename || !item?.templateType) return null;
      const config = RESOURCE_TYPES.find(
        (type) => type.value === item.templateType
      );
      if (!config) return null;
      const itemUrl = absoluteUrl(`/${config.indexUrl}/${item.filename}`);
      return {
        "@type": "ListItem",
        url: itemUrl,
        name: item.title || itemUrl,
      };
    })
    .filter((el): el is NonNullable<typeof el> => el !== null)
    .map((el, index) => ({ ...el, position: index + 1 }));

  return {
    "@context": SCHEMA_CONTEXT,
    "@type": "CollectionPage",
    "@id": absolute,
    url: absolute,
    name,
    description: description || undefined,
    isPartOf: { "@id": `${siteUrl}/${WEBSITE_ID}` },
    publisher: { "@id": `${siteUrl}/${ORG_ID}` },
    ...(elements.length
      ? {
          mainEntity: {
            "@type": "ItemList",
            itemListElement: elements,
          },
        }
      : {}),
  };
}
